import {useState} from "react";
import {defineMessage} from "react-intl";
import {ProfileSpecialDay} from "@/api";
import {Box, Checkbox, Grid, IconButton, TextField} from "@mui/material";
import {
    FormattedDialogTitle,
    FormActionButtons,
    FormattedFormRow,
    FormContainer,
    FormRowDivider,
    TimePicker,
} from "@dvrt/graphql-widgets";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import SpecialDayPicker, {getFormattedDate} from "./SpecialDayPicker";
import {DateTime} from "luxon";

type Props = {
    specialDays: ProfileSpecialDay[];
    disabled: boolean;
    updater: (newActivationTimes: ProfileSpecialDay[]) => void;
    uniqueValidator: (newActivationTimes: ProfileSpecialDay[]) => void;
};

const ProfileSpecialDayPicker = ({specialDays, disabled, updater, uniqueValidator}: Props) => {
    const [date, setDate] = useState(new Date());
    const [everyYear, setEveryYear] = useState(false);

    const onChangeEveryYear = () => {
        setEveryYear(!everyYear);

        const currentDays = structuredClone(specialDays);
        currentDays.filter((d) => d.day == getFormattedDate(date)).forEach((d) => (d.everyYear = !everyYear));
        updater(currentDays);
        uniqueValidator(currentDays);
    };

    const onAddTimeClick = (time: string) => {
        if (!specialDays.find((d) => d.day == getFormattedDate(date) && d.activationTime == time)) {
            let currentDays = structuredClone(specialDays);
            currentDays = currentDays.filter(
                (d) => d.day != getFormattedDate(date) || (d.day == getFormattedDate(date) && d.activationTime),
            );
            currentDays.push({id: null, day: getFormattedDate(date), everyYear: everyYear, activationTime: time});
            currentDays.sort((a, b) => {
                if (a.activationTime && b.activationTime) {
                    return (
                        Date.parse("1970/01/01 " + a.activationTime.slice(0, -2) + " " + a.activationTime.slice(-2)) -
                        Date.parse("1970/01/01 " + b.activationTime.slice(0, -2) + " " + b.activationTime.slice(-2))
                    );
                } else {
                    return 1;
                }
            });
            updater(currentDays);
            uniqueValidator(currentDays);
        }
    };

    const onRemoveTimeClick = (time: string) => {
        let currentDays = structuredClone(specialDays);
        currentDays = currentDays.filter(
            (d) => d.day != getFormattedDate(date) || (d.day == getFormattedDate(date) && d.activationTime != time),
        );
        updater(currentDays);
        uniqueValidator(currentDays);
    };

    const onDisableActivationClick = () => {
        let currentDays = structuredClone(specialDays);
        currentDays = currentDays.filter((d) => d.day != getFormattedDate(date));
        currentDays.push({id: null, day: getFormattedDate(date), everyYear: everyYear, activationTime: null});
        updater(currentDays);
        uniqueValidator(currentDays);
    };

    const onResetClick = () => {
        let currentDays = structuredClone(specialDays);
        currentDays = currentDays.filter((d) => d.day != getFormattedDate(date));
        updater(currentDays);
        uniqueValidator(currentDays);
    };

    const onChangeDate = (newDate: Date) => {
        setDate(newDate);
        setEveryYear(currentDateData(newDate).length ? currentDateData(newDate)[0].everyYear : false);
    };

    const currentDateData = (newDate: Date): ProfileSpecialDay[] => {
        return specialDays.filter((d) => d.day == getFormattedDate(newDate));
    };

    return (
        <Grid container direction="row">
            <Grid size="auto">
                <SpecialDayPicker
                    value={date}
                    disabled={disabled}
                    onChange={onChangeDate}
                    specialDays={specialDays.map((d) => ({day: d.day, everyYear: d.everyYear}))}
                />
            </Grid>
            <Grid size="grow">
                <FormattedDialogTitle
                    title={defineMessage({id: "specialDays.profile.title", defaultMessage: "Activation on {date}"})}
                    values={{date: DateTime.fromJSDate(date).toLocaleString(DateTime.DATE_MED)}}
                />
                <FormContainer style={{gridTemplateColumns: "0.5fr 2.5fr", minWidth: "200px", width: "100%"}}>
                    <FormattedFormRow
                        label={defineMessage({id: "specialDays.profile.everyYear", defaultMessage: "Every year"})}
                    >
                        <Checkbox
                            value={currentDateData(date).length ? currentDateData(date)[0].everyYear : everyYear}
                            disabled={disabled}
                            checked={currentDateData(date).length ? currentDateData(date)[0].everyYear : everyYear}
                            onChange={onChangeEveryYear}
                            color="primary"
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "specialDays.profile.times", defaultMessage: "Times"})}>
                        <Grid container>
                            <Grid size={7}>
                                {currentDateData(date).length ? (
                                    specialDays
                                        .filter((d) => d.day == getFormattedDate(date))
                                        .map((d, index) => (
                                            <Grid container key={index}>
                                                <Grid size={7}>
                                                    <TextField
                                                        variant="standard"
                                                        disabled
                                                        value={d.activationTime ? d.activationTime : "No activation"}
                                                    />
                                                </Grid>
                                                <Grid size={1}>
                                                    <IconButton
                                                        color="secondary"
                                                        size="small"
                                                        onClick={() => onRemoveTimeClick(d.activationTime)}
                                                    >
                                                        <FontAwesomeIcon icon="minus" />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        ))
                                ) : (
                                    <TextField variant="standard" disabled value="Default" />
                                )}
                            </Grid>
                            <TimePicker
                                value={"00:00"}
                                onChange={onAddTimeClick}
                                minutesStep={15}
                                disabled={disabled}
                            />
                        </Grid>
                    </FormattedFormRow>
                    <FormRowDivider />
                    <Box gridColumn="1/span 2">
                        <FormActionButtons
                            mainAction={onResetClick}
                            secondaryAction={onDisableActivationClick}
                            mainButtonText={defineMessage({
                                id: "specialDays.profile.reset",
                                defaultMessage: "Reset to default",
                            })}
                            secondaryButtonText={defineMessage({
                                id: "specialDays.profile.noActivation",
                                defaultMessage: "No activation",
                            })}
                            mainButtonEnabled={!disabled && !!currentDateData(date).length}
                            secondaryButtonEnabled={
                                !disabled && (!currentDateData(date).length || currentDateData(date)[0].activationTime)
                            }
                        />
                    </Box>
                </FormContainer>
            </Grid>
        </Grid>
    );
};

export default ProfileSpecialDayPicker;
